import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Cardinal } from '../types';
import cardinauxData from '../data/cardinaux_with_bios.json';

interface CardRevealProps {
  data: Cardinal;
  isRevealed?: boolean;
}

const loadedCardinaux: Cardinal[] = cardinauxData.map((cardinal: any) => ({
  url: cardinal.url || '',
  imageUrl: cardinal.imageUrl || null,
  coatOfArmsUrl: cardinal.coatOfArmsUrl || null,
  name: cardinal.name || 'Cardinal Inconnu',
  flagUrl: cardinal.flagUrl || null,
  elector: cardinal.elector || null,
  birthDate: cardinal.birthDate || null,
  creationDate: cardinal.creationDate || null,
  createdByFrancis: cardinal.createdByFrancis !== undefined ? cardinal.createdByFrancis : null,
  function: cardinal.function || null,
  country: cardinal.country || null,
  continent: cardinal.continent || null,
  title: cardinal.title || null,
  age: cardinal.age || null,
  biography: cardinal.biography || null
}));

const CardReveal: React.FC<CardRevealProps> = ({ data, isRevealed: initialIsRevealed = true }) => {
  const [isRevealed, setIsRevealed] = useState(initialIsRevealed);
  const [showBio, setShowBio] = useState(false);
  const [typedBio, setTypedBio] = useState('');
  const [typingIndex, setTypingIndex] = useState(0);
  const bioButtonRef = useRef<HTMLButtonElement>(null);
  
  // Fonction pour déterminer quel pape a créé le cardinal
  const getCreatorPope = (creationDate: string, createdByFrancis: boolean): string => {
    if (createdByFrancis) return "Créé par le Pape François";
    
    if (!creationDate) return "Date de création inconnue";
    
    // Convertir la date au format JJ/MM/AAAA en objet Date
    const parts = creationDate.split('/');
    if (parts.length !== 3) return "Date de création invalide";
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Les mois commencent à 0 en JavaScript
    const year = parseInt(parts[2], 10);
    
    const date = new Date(year, month, day);
    
    // Dates des pontificats
    const benedictXVIStart = new Date(2005, 3, 19); // 19 avril 2005
    const benedictXVIEnd = new Date(2013, 1, 28);   // 28 février 2013
    
    if (date >= benedictXVIStart && date <= benedictXVIEnd) {
      return "Créé par le Pape Benoît XVI";
    } else if (date < benedictXVIStart) {
      return "Créé par le Pape Jean-Paul II";
    } else {
      return "Créé par le Pape François";
    }
  };

  // Fonction pour calculer l'âge à partir de la date de naissance
  const calculateAge = (birthDate: string | null | undefined): number | null => {
    if (!birthDate) return null;
    
    const birth = new Date(birthDate);
    // Vérifier si la date est valide
    if (isNaN(birth.getTime())) return null;
    
    const today = new Date();
    
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    // Si le mois de naissance n'est pas encore arrivé cette année ou 
    // si c'est le même mois mais que le jour n'est pas encore arrivé
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  // Fonction pour formater la date
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Non disponible';
    
    // Vérifier si la date est au format français (JJ/MM/AAAA)
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/').map(part => parseInt(part, 10));
      // Créer une date au format JavaScript (les mois commencent à 0 en JavaScript)
      const date = new Date(year, month - 1, day);
      
      // Vérifier si la date est valide
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      }
    }
    
    // Essayer le format standard
    const date = new Date(dateString);
    // Vérifier si la date est valide
    if (isNaN(date.getTime())) return 'Date non disponible';
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Utiliser useEffect pour ajouter un écouteur d'événement au bouton après le rendu
  useEffect(() => {
    const bioButton = bioButtonRef.current;
    if (bioButton) {
      const clickHandler = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        toggleBio();
      };
      
      bioButton.addEventListener('click', clickHandler);
      
      return () => {
        bioButton.removeEventListener('click', clickHandler);
      };
    }
  }, [bioButtonRef.current]);

  // Fonction pour basculer l'affichage de la biographie
  const toggleBio = () => {
    setShowBio(prevState => !prevState);
    
    if (!showBio && data.biography) {
      setTypedBio('');
      setTypingIndex(0);
      
      // Ajouter des sauts de ligne pour aérer la biographie
      let formattedBio = data.biography;
      // Remplacer les points suivis d'un espace par un point et un saut de ligne
      formattedBio = formattedBio.replace(/\. /g, '.\n\n');
      // Remplacer les points d'exclamation suivis d'un espace par un point d'exclamation et un saut de ligne
      formattedBio = formattedBio.replace(/! /g, '!\n\n');
      // Remplacer les points d'interrogation suivis d'un espace par un point d'interrogation et un saut de ligne
      formattedBio = formattedBio.replace(/\? /g, '?\n\n');
      
      // Effet de frappe pour la biographie
      const typingInterval = setInterval(() => {
        setTypingIndex(prev => {
          const nextIndex = prev + 1;
          if (nextIndex <= formattedBio.length) {
            setTypedBio(formattedBio.substring(0, nextIndex));
            return nextIndex;
          } else {
            clearInterval(typingInterval);
            return prev;
          }
        });
      }, 2); // Vitesse de frappe
    }
  };

  return (
    <motion.div
      className="relative w-full max-w-4xl mx-auto perspective px-4 sm:px-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex flex-col md:flex-row items-start">
        <motion.div
          className={`w-full ${showBio ? 'md:w-1/2' : 'md:w-full'} rounded-xl overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 shadow-2xl border border-gray-700`}
          initial={{ rotateY: 180, opacity: 0 }}
          animate={{ 
            rotateY: isRevealed ? 0 : 180,
            opacity: isRevealed ? 1 : 0,
            boxShadow: isRevealed ? "0 0 30px 5px rgba(255, 215, 0, 0.2)" : "none",
            x: showBio ? 0 : 0
          }}
          transition={{ 
            type: "spring", 
            stiffness: 50, 
            damping: 20,
            duration: 0.8 
          }}
          style={{ 
            height: 'fit-content',
            position: 'relative',
            top: 'auto'
          }}
        >
          <div className="relative">
            {/* Bannière supérieure avec drapeau et blason */}
            <div className="relative h-32 bg-gradient-to-r from-red-600 to-purple-800 overflow-hidden">
              {/* Effet de lumière animé */}
              <motion.div 
                className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-600 opacity-20"
                animate={{ 
                  x: ['-100%', '100%'],
                  opacity: [0.1, 0.3, 0.1]
                }}
                transition={{ 
                  duration: 5, 
                  repeat: Infinity,
                  repeatType: "mirror"
                }}
              />
              
              {/* Drapeau */}
              {data.flagUrl && (
                <div className="absolute top-4 right-4 w-20 h-12 shadow-lg rounded-md overflow-hidden border-2 border-yellow-500">
                  <img 
                    src={data.flagUrl} 
                    alt="Drapeau" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/No-image-available.png/200px-No-image-available.png";
                    }}
                  />
                  {data.country && (
                    <div className="absolute bottom-0 inset-x-0 bg-black bg-opacity-70 text-white text-xs text-center py-1">
                      {data.country}
                    </div>
                  )}
                </div>
              )}
              
              {/* Blason */}
              {data.coatOfArmsUrl && (
                <div className="absolute top-4 left-4 w-20 h-20 shadow-lg rounded-full overflow-hidden border-2 border-yellow-500 bg-white">
                  <img 
                    src={data.coatOfArmsUrl} 
                    alt="Blason" 
                    className="w-full h-full object-contain p-1"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/No-image-available.png/200px-No-image-available.png";
                    }}
                  />
                </div>
              )}
            </div>
            
            {/* Photo du cardinal */}
            <div className="flex justify-center">
              <div className="relative w-40 h-40 rounded-full overflow-hidden border-4 border-yellow-500 -mt-20 shadow-xl bg-gradient-to-br from-gray-700 to-gray-800">
                {data.imageUrl ? (
                  <img 
                    src={data.imageUrl} 
                    alt={data.name} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0a/No-image-available.png/200px-No-image-available.png";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-4xl font-bold">
                    {data.name.substring(0, 2).toUpperCase()}
                  </div>
                )}
                
                {/* Effet de brillance */}
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0"
                  animate={{ 
                    x: ['-200%', '200%'],
                    opacity: [0, 0.3, 0]
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity,
                    repeatDelay: 5
                  }}
                />
              </div>
            </div>
            
            {/* Informations du cardinal */}
            <div className="p-6 pt-4">
              <motion.div
                className="text-center mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <h2 className="inline-flex items-center text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
                  {data.name}
                  {data.elector === 'E' && (
                    <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500 text-black">
                      Électeur
                    </span>
                  )}
                </h2>

                
                {/* Fonction sous le nom */}
                {data.function && (
                  <p className="text-gray-300 mt-1 text-center">
                    {data.function}
                  </p>
                )}
              </motion.div>
              
              <motion.div 
                className="space-y-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                {/* Informations personnelles */}
                <div className="flex items-center  p-2 bg-gradient-to-r from-gray-700 to-gray-800 rounded-lg">
                  <div className="ml-2">
                    <span className="text-sm text-gray-400">Date de naissance:</span>
                    <p className="text-white">
                      {formatDate(data.birthDate)} 
                      {data.age ? ` (${data.age} ans)` : '-'}
                    </p>
                    {data.birthPlace && (
                      <p className="text-gray-300 text-xs">{data.birthPlace}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center p-2 bg-gradient-to-r from-gray-700 to-gray-800 rounded-lg">
                  <div className="ml-2">
                    <span className="text-sm text-gray-400">Cardinal depuis le :</span>
                    <p className="text-white">{formatDate(data.creationDate)}</p>
                  </div>
                </div>
                
                {data.creationDate && (
                  <div className="flex items-center p-2 bg-gradient-to-r from-gray-700 to-gray-800 rounded-lg">
                    <div className="ml-2">
                      <p className="text-white">{getCreatorPope(data.creationDate, data.createdByFrancis || false)}</p>
                    </div>
                  </div>
                )}
                
                {data.continent && (
                  <div className="flex items-center p-2 bg-gradient-to-r from-gray-700 to-gray-800 rounded-lg">
                    <svg className="w-5 h-5 mr-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <span className="text-sm text-gray-400">Continent:</span>
                      <p className="text-white">{data.continent}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
            
            {/* Pied de carte avec lien pour en savoir plus */}
            <div className="p-4 bg-gradient-to-r from-gray-800 to-gray-900 border-t border-gray-700 flex justify-center relative z-10">
              <button 
                ref={bioButtonRef}
                className="text-yellow-400 text-lg font-bold underline hover:text-yellow-800 transition-colors flex items-center cursor-pointer p-2"
                style={{ pointerEvents: 'auto' }}
              >
                {showBio ? (
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                )}
                {showBio ? "Masquer la biographie" : "Découvrir la biographie du Pape"}
              </button>
            </div>
          </div>
        </motion.div>
        
        {/* Section biographie qui apparaît à droite */}
        {showBio && (
          <motion.div
            className="w-full md:w-1/2 md:pl-6 mt-4 md:mt-0"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 shadow-2xl border border-gray-700 flex flex-col">
              <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 mb-4">
                Biographie de {data.name}
              </h3>
              <div 
                className="text-gray-300 text-sm leading-relaxed whitespace-pre-line"
              >
                {data.biography ? (
                  <div className="biography-content">
                    {typedBio}
                    <motion.span 
                      className="inline-block w-2 h-4 bg-yellow-400"
                      animate={{ opacity: [1, 0, 1] }}
                      transition={{ duration: 0.8, repeat: Infinity }}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <svg className="w-16 h-16 text-gray-500 mb-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <p className="text-lg font-semibold text-gray-400">Biographie non disponible</p>
                    <p className="mt-2 text-gray-500">
                      Les informations biographiques pour {data.name} ne sont pas encore disponibles.
                    </p>
                    {data.url && (
                      <a 
                        href={data.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Consulter sa page Wikipedia
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Carte verso (quand non révélée) */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-red-600 to-purple-800 rounded-xl flex items-center justify-center"
          initial={{ rotateY: 0, opacity: 1 }}
          animate={{ 
            rotateY: isRevealed ? -180 : 0,
            opacity: isRevealed ? 0 : 1 
          }}
          transition={{ 
            type: "spring", 
            stiffness: 50, 
            damping: 20,
            duration: 0.8 
          }}
        >
          <div className="relative w-full h-full">
            {/* Motif décoratif */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute inset-4 border-4 border-yellow-500 rounded-lg opacity-30" />
              <motion.div 
                className="absolute inset-0"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <div className="absolute top-1/2 left-1/2 w-64 h-64 -ml-32 -mt-32 border-2 border-yellow-500 rounded-full opacity-20" />
                <div className="absolute top-1/2 left-1/2 w-48 h-48 -ml-24 -mt-24 border-2 border-yellow-500 rounded-full opacity-20" />
                <div className="absolute top-1/2 left-1/2 w-32 h-32 -ml-16 -mt-16 border-2 border-yellow-500 rounded-full opacity-20" />
              </motion.div>
            </div>
            
            {/* Logo central */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                animate={{ 
                  scale: [1, 1.05, 1],
                  opacity: [0.8, 1, 0.8]
                }}
                transition={{ 
                  duration: 2, 
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <svg 
                  className="w-32 h-32 text-yellow-500" 
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm6 9.09c0 4-2.55 7.7-6 8.83-3.45-1.13-6-4.82-6-8.83v-4.7l6-2.25 6 2.25v4.7z"/>
                  <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0 8.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z"/>
                </svg>
              </motion.div>
            </div>
            
            {/* Texte */}
            <div className="absolute bottom-10 inset-x-0 text-center">
              <motion.h3
                className="text-2xl font-bold text-white mb-2"
                animate={{ 
                  y: [0, -5, 0],
                  opacity: [0.9, 1, 0.9]
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                Cardinal
              </motion.h3>
              <motion.p
                className="text-yellow-300 text-sm"
                animate={{ 
                  opacity: [0.7, 1, 0.7]
                }}
                transition={{ 
                  duration: 2, 
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: 0.5
                }}
              >
                Cliquez pour révéler
              </motion.p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default CardReveal;
